import { useMutation, useQuery } from '@apollo/client';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Page from 'components/Page';
import EditPageCard from 'lib/components/EditPageCard';
import { dateTimeFormat } from 'lib/formatters';
import { UserContext } from 'lib/UserContext';
import { FC, ReactNode, useContext, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  crashReportDeleteMutation,
  crashReportQuery,
  crashReportsQuery,
} from './queries';
import {
  CrashReportDeleteMutation,
  CrashReportDeleteMutationVariables,
  CrashReportQuery,
  CrashReportQueryVariables,
} from './types/queries';
import CrashReportTriggerIcon from './CrashReportTriggerIcon';
import { formatSize } from './formatters';

const Detail: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Grid item xs={12} sm={6} lg={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography sx={{ overflowWrap: 'anywhere' }}>{children}</Typography>
  </Grid>
);

const CrashReportDetailPage: FC = () => {
  const { id = '' } = useParams<'id'>();
  const nav = useNavigate();
  const { user } = useContext(UserContext);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [mutationError, setMutationError] = useState('');
  const { data, loading, error } = useQuery<
    CrashReportQuery,
    CrashReportQueryVariables
  >(crashReportQuery, { variables: { id } });
  const [remove, { loading: deleting }] = useMutation<
    CrashReportDeleteMutation,
    CrashReportDeleteMutationVariables
  >(crashReportDeleteMutation, {
    refetchQueries: [{ query: crashReportsQuery }],
    onError: (mutationFailure) => setMutationError(mutationFailure.message),
  });

  const report = data?.crashReport;

  return (
    <Page
      title="Crash Report"
      backButton="/admin/crash_reports"
      options={
        report && (
          <Button
            component="a"
            href={`/api/crash-reports/${report.id}/download`}
            color="inherit"
            startIcon={<DownloadIcon />}
          >
            Download
          </Button>
        )
      }
    >
      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error.message}</Alert>}
      {!loading && !error && !report && (
        <Alert severity="warning">The crash report was not found.</Alert>
      )}
      {report && (
        <EditPageCard
          fullWidth
          title={report.fileName}
          error={mutationError}
          actions={
            <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
              <Button
                component="a"
                href={`/api/crash-reports/${report.id}/download`}
                variant="contained"
                startIcon={<DownloadIcon />}
              >
                Download crash dump
              </Button>
              {user?.isAdmin && (
                <Button
                  sx={{ ml: 'auto' }}
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              )}
            </Box>
          }
        >
          <Grid container spacing={3}>
            <Detail label="Owner">
              <Link
                component={RouterLink}
                to={`/admin/owners/${report.server.owner.id}`}
              >
                {report.server.owner.name}
              </Link>
            </Detail>
            <Detail label="Server">{report.server.name}</Detail>
            <Detail label="Trigger">
              <CrashReportTriggerIcon trigger={report.trigger} />
            </Detail>
            <Detail label="Captured">
              {dateTimeFormat(report.capturedAt)}
            </Detail>
            <Detail label="Received">
              {dateTimeFormat(report.receivedAt)}
            </Detail>
            <Detail label="WebServer version">
              {report.version.versionString}
            </Detail>
            <Detail label="Legacy server version">
              {report.legacyServerVersion.version}
            </Detail>
            <Detail label="Legacy server ID">{report.legacyServerId}</Detail>
            <Detail label="Legacy server number">
              {report.legacyServerNumber}
            </Detail>
            <Detail label="Exit code">{report.exitCode}</Detail>
            <Detail label="Archive size">{formatSize(report.size)}</Detail>
            <Detail label="SHA-256">
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                {report.sha256}
              </Box>
            </Detail>
          </Grid>
        </EditPageCard>
      )}
      {report && (
        <EditPageCard
          fullWidth
          title={`Files (${report.files.length})`}
          sx={{ mt: 3 }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Path</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Compressed size</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.files.map((file, index) => (
                  <TableRow key={`${file.path}-${index}`}>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>
                      {file.path}
                    </TableCell>
                    <TableCell align="right">{formatSize(file.size)}</TableCell>
                    <TableCell align="right">
                      {formatSize(file.compressedSize)}
                    </TableCell>
                  </TableRow>
                ))}
                {report.files.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      This crash dump does not contain any files.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </EditPageCard>
      )}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete crash report?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the crash report and its stored archive.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={async () => {
              const result = await remove({ variables: { id } });
              if (result.data?.crashReportDelete) nav('/admin/crash_reports');
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default CrashReportDetailPage;
